/**
 * DashboardToolbar — controls bar above the dashboard grid.
 * Provides edit mode toggle, widget catalog, save, and global settings.
 */

import {
  Pencil,
  Check,
  Plus,
  Save,
  Calendar,
  LayoutDashboard,
} from 'lucide-react'
import { Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Spinner } from '@/components/ui'
import { CampaignSelectionChip } from '@/components/marketing/CampaignSelectionChip'
import type { DashboardSummary } from '@/types/dashboard'

interface DashboardToolbarProps {
  dashboardName: string
  globalDateRange: number
  isEditMode: boolean
  isDirty: boolean
  isSaving: boolean
  dashboards: DashboardSummary[]
  currentDashboardId: string | null
  onToggleEditMode: () => void
  onOpenCatalog: () => void
  onSave: () => void
  onDateRangeChange: (days: number) => void
  onSwitchDashboard: (id: string) => void
  onNewDashboard: () => void
}

export function DashboardToolbar({
  dashboardName,
  globalDateRange,
  isEditMode,
  isDirty,
  isSaving,
  dashboards,
  currentDashboardId,
  onToggleEditMode,
  onOpenCatalog,
  onSave,
  onDateRangeChange,
  onSwitchDashboard,
  onNewDashboard,
}: DashboardToolbarProps) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        {/* Dashboard switcher */}
        {dashboards.length > 1 && (
          <Select
            value={currentDashboardId || ''}
            onValueChange={onSwitchDashboard}
          >
            <SelectTrigger className="w-48">
              <LayoutDashboard className="w-4 h-4 mr-2 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dashboards.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {dashboards.length <= 1 && (
          <h2 className="text-lg font-semibold truncate">{dashboardName}</h2>
        )}

        {/* Global date range */}
        <Select
          value={String(globalDateRange)}
          onValueChange={(v) => onDateRangeChange(parseInt(v))}
        >
          <SelectTrigger className="w-36">
            <Calendar className="w-4 h-4 mr-2 shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>

        {/* Cross-widget campaign selection (hidden when empty) */}
        <CampaignSelectionChip />
      </div>

      <div className="flex items-center gap-2">
        {isEditMode && (
          <>
            <Button variant="secondary" size="sm" onClick={onOpenCatalog}>
              <Plus className="w-4 h-4 mr-1.5" />
              Add Widget
            </Button>
            <Button variant="secondary" size="sm" onClick={onNewDashboard}>
              <LayoutDashboard className="w-4 h-4 mr-1.5" />
              New Dashboard
            </Button>
          </>
        )}

        {isDirty && (
          <Button size="sm" onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : (
              <Save className="w-4 h-4 mr-1.5" />
            )}
            Save
          </Button>
        )}

        <Button
          variant={isEditMode ? 'default' : 'secondary'}
          size="sm"
          onClick={onToggleEditMode}
        >
          {isEditMode ? (
            <>
              <Check className="w-4 h-4 mr-1.5" />
              Done
            </>
          ) : (
            <>
              <Pencil className="w-4 h-4 mr-1.5" />
              Edit Layout
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
